import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { ModernButton } from '../../components/ui/ModernButton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PriorityIndicator, CountIndicator, InsightPriorityIndicator } from '../../components/ui/ModernIndicators';
import { Dialog } from 'primereact/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { AgentDashboardStats, AgentInsight, CustomerInfo } from '../../types/agent';
import { CustomerService } from '../../services/customerService';
import { InsightService } from '../../services/insightService';
import { NotificationService } from '../../services/notificationService';
import { ActivityService } from '../../services/activityService';
import { StatsSkeleton } from '../../components/ui/LoadingComponents';
import { LeadBrief } from '../../components/LeadBrief';
import { getAgentDisplayName } from '../../utils/stringHelpers';
import { Timeline } from 'primereact/timeline';
import './DashboardPage.css';

interface ActivityItem {
	id: string;
	icon: string;
	color: string;
	date: Date;
	title: string;
	description: string;
	entityType: string;
	entityId: string;
	activityType: string;
}

export const DashboardPage: React.FC = () => {
	const { currentAgent, loading: authLoading } = useAuth();
	const navigate = useNavigate();
	const [stats, setStats] = useState<AgentDashboardStats>({
		totalCustomers: 0,
		activeInsights: 0,
		urgentInsights: 0,
		conversionsThisWeek: 0,
		averageLeadScore: 0,
		responseRate: 0
	});
	const [unreadNotifications, setUnreadNotifications] = useState(0);
	const [recentInsights, setRecentInsights] = useState<AgentInsight[]>([]);
	const [urgentInsights, setUrgentInsights] = useState<AgentInsight[]>([]);
	const [unviewedInsights, setUnviewedInsights] = useState<AgentInsight[]>([]);
	const [followUpsdue, setFollowUpsDue] = useState<AgentInsight[]>([]);
	const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedInsight, setSelectedInsight] = useState<AgentInsight | null>(null);
	const [showDetailDialog, setShowDetailDialog] = useState(false);

	useEffect(() => {
		const loadDashboardData = async () => {
			// Don't try to load data while auth is still loading
			if (authLoading) {
				return;
			}

			if (!currentAgent?.id) {
				console.error('DashboardPage: No agent ID found');
				setError('No agent ID found. Please ensure you are logged in.');
				setLoading(false);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const isPrivileged = currentAgent.role === 'master_agent' || currentAgent.role === 'super_agent';

				// The insight set fetched here is deliberately much larger than the
				// ten rows the table shows. Every derived figure below - conversions
				// this week, urgent, unviewed, follow-ups due - was being filtered
				// out of a 10-row page, so an agent who converted five leads saw
				// "0 conversions this week" unless those five happened to be among
				// the ten most recent insights.
				const [customerCounts, workingSet, insightStats, notificationStats] = await Promise.all([
					// A count, not a page of records - the tile only renders a number.
					CustomerService.getCustomerCounts(),

					isPrivileged
						? InsightService.getAllInsights({ limit: 500 })
						: InsightService.getAgentInsights(currentAgent.id, { limit: 500 }),

					InsightService.getInsightStats(isPrivileged ? undefined : currentAgent.id),

					NotificationService.getNotificationStats(currentAgent.id)
				]);

				// What the "Recent Insights" table renders.
				const insights = workingSet.slice(0, 10);

				// Set unread notifications count
				setUnreadNotifications(notificationStats.unread);

				// Calculate conversions this week
				const oneWeekAgo = new Date();
				oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

				const conversionsThisWeek = workingSet.filter(i =>
					i.status === 'converted' &&
					i.resolvedAt &&
					i.resolvedAt >= oneWeekAgo
				).length;

				// Set dashboard stats
				setStats({
					totalCustomers: isPrivileged
						? customerCounts.total
						: (customerCounts.byAgent[currentAgent.id] || 0),
					activeInsights: insightStats.totalInsights - insightStats.newInsights,
					urgentInsights: insightStats.urgentInsights,
					conversionsThisWeek,
					averageLeadScore: Math.round(insightStats.averageLeadScore),
					responseRate: Math.round(insightStats.conversionRate)
				});

				setRecentInsights(insights);

				// Get actionable insights
				const now = new Date();
				const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
				
				// Urgent insights that need immediate attention
				const urgent = workingSet.filter(i => 
					i.priority === 'urgent' && 
					i.status === 'new'
				).slice(0, 5);
				setUrgentInsights(urgent);

				// Unviewed insights
				const unviewed = workingSet.filter(i => !i.viewed).slice(0, 5);
				setUnviewedInsights(unviewed);

				// Follow-ups due (insights expiring soon or in-progress)
				const followUps = workingSet.filter(i => {
					const expiresAt = i.expiresAt instanceof Date ? i.expiresAt : new Date(i.expiresAt);
					return (i.status === 'in_progress' || i.status === 'contacted') && 
						   expiresAt <= threeDaysFromNow;
				}).slice(0, 5);
				setFollowUpsDue(followUps);

			// Load recent activity from ActivityService
			const recentActivities = await ActivityService.getRecentActivities(currentAgent.id, 5);
			
			// Transform Activity objects to ActivityItem format for the Timeline component
			const activities: ActivityItem[] = recentActivities.map(activity => {
				const { icon, color } = ActivityService.getActivityIcon(activity.activityType);
				return {
					id: activity.id,
					icon,
					color,
					date: activity.timestamp,
					title: activity.activityType.split('_').map(word => 
						word.charAt(0).toUpperCase() + word.slice(1)
					).join(' '),
					description: ActivityService.formatActivityDescription(activity),
					entityType: activity.entityType,
					entityId: activity.entityId,
					activityType: activity.activityType
				};
			});
			
			setRecentActivity(activities);

			} catch (error) {
				console.error('DashboardPage: Dashboard loading error:', error);
				console.error('DashboardPage: Error stack:', error instanceof Error ? error.stack : 'No stack');
				if (error instanceof Error) {
					setError(`Failed to load dashboard data: ${error.message}`);
				} else {
					setError('Failed to load dashboard data. Please try again later.');
				}
			} finally {
				setLoading(false);
			}
		};

		loadDashboardData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authLoading, currentAgent]);


	const getPriorityBadge = (priority: string | number | null | undefined) => {
		const normalizedPriority = String(priority || 'medium').toLowerCase();
		const validPriority = ['urgent', 'high', 'medium', 'low'].includes(normalizedPriority) 
			? normalizedPriority 
			: 'medium';
		return <PriorityIndicator priority={validPriority as 'urgent' | 'high' | 'medium' | 'low'} compact={true} />;
	};

	const getTimeAgo = (date: Date) => {
		const minutes = Math.floor((Date.now() - date.getTime()) / (1000 * 60));
		if (minutes < 60) return `${minutes}m ago`;
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return `${hours}h ago`;
		const days = Math.floor(hours / 24);
		return `${days}d ago`;
	};

	const formatTimestamp = (date: Date) => {
		const now = new Date();
		const isToday = date.toDateString() === now.toDateString();
		const yesterday = new Date(now);
		yesterday.setDate(yesterday.getDate() - 1);
		const isYesterday = date.toDateString() === yesterday.toDateString();

		const timeStr = date.toLocaleTimeString('en-US', { 
			hour: 'numeric', 
			minute: '2-digit',
			hour12: true 
		});

		if (isToday) return `Today at ${timeStr}`;
		if (isYesterday) return `Yesterday at ${timeStr}`;
		
		return date.toLocaleDateString('en-US', { 
			month: 'short', 
			day: 'numeric',
			hour: 'numeric', 
			minute: '2-digit',
			hour12: true 
		});
	};

	const handleActivityClick = (activity: ActivityItem) => {
		// Navigate to the appropriate page based on entity type
		switch (activity.entityType) {
			case 'customer':
				navigate(`/agent/customers/${activity.entityId}`);
				break;
			case 'insight':
				navigate('/agent/insights');
				// Could add a query param or state to highlight specific insight
				break;
			case 'notification':
				navigate('/agent/notifications');
				break;
			case 'agent':
				navigate('/agent/agents');
				break;
			default:
				break;
		}
	};

	const handleViewInsight = async (insight: AgentInsight) => {
		setSelectedInsight(insight);
		setShowDetailDialog(true);

		if (!insight.viewed) {
			try {
				await InsightService.markInsightAsViewed(insight.id);
				// Reload insights to update the viewed status
				const updatedInsights = [...recentInsights];
				const insightIndex = updatedInsights.findIndex(i => i.id === insight.id);
				if (insightIndex !== -1) {
					updatedInsights[insightIndex] = { ...updatedInsights[insightIndex], viewed: true } as AgentInsight;
					setRecentInsights(updatedInsights);
				}
			} catch (error) {
				// Silently fail - not critical
			}
		}

		// Log activity
		if (currentAgent?.id) {
			await ActivityService.logActivity(
				currentAgent.id,
				'insight_viewed',
				'insight',
				insight.id,
				insight.title,
				{
					insightPriority: insight.priority,
					insightType: insight.type,
					customerEmail: insight.customerInfo?.email
				}
			);
		}
	};

	const handleCallCustomer = (phone: string) => {
		window.location.href = `tel:${phone}`;
	};

	const handleEmailCustomer = (email: string) => {
		window.location.href = `mailto:${email}`;
	};

	const getCustomerDisplayName = (customerInfo: CustomerInfo | undefined) => {
		if (!customerInfo) return '-';

		const firstName = customerInfo.firstName?.trim() || '';
		const lastName = customerInfo.lastName?.trim() || '';

		// If both names are empty or "Unknown", use email
		if ((!firstName && !lastName) ||
			(firstName.toLowerCase() === 'unknown' && lastName.toLowerCase() === 'unknown')) {
			return customerInfo.email || '-';
		}

		// Otherwise return the full name
		return `${firstName} ${lastName}`.trim() || customerInfo.email || '-';
	};

	// Show loading state while auth is loading
	if (authLoading) {
		return (
			<div className="dashboard-page">
				<div className="dashboard-welcome">
					<h1>Loading...</h1>
					<p>Authenticating user...</p>
				</div>
				<StatsSkeleton count={4} />
			</div>
		);
	}

	if (error) {
		return (
			<div className="dashboard-page">
				<Card className="error-card">
					<div className="error-content">
						<i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
						<h3>Error Loading Dashboard</h3>
						<p>{error}</p>
						<ModernButton variant="primary" onClick={() => window.location.reload()}>
							Retry
						</ModernButton>
					</div>
				</Card>
			</div>
		);
	}

	return (
		<div className="dashboard-page">
			<div className="dashboard-welcome">
				<h1>Welcome back, {getAgentDisplayName(currentAgent)?.split(' ')[0] || 'Agent'}!</h1>
				<p>Here's what's happening with your customers today.</p>
			</div>

			{/* Key Metrics */}
			{loading ? (
				<StatsSkeleton count={4} />
			) : (
				<div className="metrics-grid">
					<Card className="metric-card">
						<div className="metric-content">
							<div className="metric-icon metric-customers-icon">
								<i className="pi pi-users"></i>
							</div>
							<div className="metric-details">
								<h3>{stats.totalCustomers}</h3>
								<p>Total Customers</p>
							</div>
						</div>
					</Card>

					<Card className="metric-card">
						<div className="metric-content">
							<div className="metric-icon metric-insights-icon">
								<i className="pi pi-exclamation-triangle"></i>
							</div>
							<div className="metric-details">
								<h3>{stats.activeInsights}</h3>
								<p>Active Insights</p>
								{stats.urgentInsights > 0 && (
									<CountIndicator count={stats.urgentInsights} label="urgent" />
								)}
							</div>
						</div>
					</Card>

					<Card className="metric-card">
						<div className="metric-content">
							<div className="metric-icon metric-conversions-icon">
								<i className="pi pi-check-circle"></i>
							</div>
							<div className="metric-details">
								<h3>{stats.conversionsThisWeek}</h3>
								<p>Conversions This Week</p>
							</div>
						</div>
					</Card>

					<Card className="metric-card metric-clickable" onClick={() => navigate('/agent/notifications')}>
						<div className="metric-content">
							<div className="metric-icon metric-notifications-icon">
								<i className="pi pi-bell"></i>
							</div>
							<div className="metric-details">
								<h3>{unreadNotifications}</h3>
								<p>Unread Notifications</p>
								{unreadNotifications > 0 && (
									<ModernButton variant="ghost" size="sm" style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem' }}>
										View All →
									</ModernButton>
								)}
							</div>
						</div>
					</Card>
				</div>
			)}

			{/* Actionable Sections */}
			<div className="actionable-grid">
				{/* Urgent Actions */}
				<Card className="actionable-card urgent-card">
					<div className="card-header">
						<h3>
							<i className="pi pi-exclamation-circle" style={{ marginRight: '0.5rem', color: '#e74c3c' }}></i>
							Urgent Actions
						</h3>
						<CountIndicator count={urgentInsights.length} label="urgent" />
					</div>
					{loading ? (
						<div className="actionable-loading">Loading...</div>
					) : urgentInsights.length === 0 ? (
						<div className="empty-state">
							<i className="pi pi-check-circle" style={{ fontSize: '2rem', color: '#2ecc71' }}></i>
							<p>No urgent actions - you're all caught up!</p>
						</div>
					) : (
						<div className="actionable-list">
							{urgentInsights.map(insight => (
								<div key={insight.id} className="actionable-item">
									<div className="actionable-info">
										<div className="actionable-title">{insight.title}</div>
										<div className="actionable-customer">
											{getCustomerDisplayName(insight.customerInfo)}
										</div>
									</div>
									<div className="actionable-actions">
										<ModernButton
											variant="danger"
											size="sm"
											icon="pi pi-phone"
											onClick={() => insight.customerInfo?.phone && handleCallCustomer(insight.customerInfo.phone)}
											disabled={!insight.customerInfo?.phone}
										>
											Call Now
										</ModernButton>
										<ModernButton
											variant="ghost"
											size="sm"
											icon="pi pi-eye"
											onClick={() => handleViewInsight(insight)}
										/>
									</div>
								</div>
							))}
						</div>
					)}
				</Card>

				{/* Follow-ups Due */}
				<Card className="actionable-card followup-card">
					<div className="card-header">
						<h3>
							<i className="pi pi-clock" style={{ marginRight: '0.5rem', color: '#f39c12' }}></i>
							Follow-ups Due
						</h3>
						<CountIndicator count={followUpsdue.length} />
					</div>
					{loading ? (
						<div className="actionable-loading">Loading...</div>
					) : followUpsdue.length === 0 ? (
						<div className="empty-state">
							<i className="pi pi-calendar-times" style={{ fontSize: '2rem', color: '#95a5a6' }}></i>
							<p>No follow-ups due in the next 3 days</p>
						</div>
					) : (
						<div className="actionable-list">
							{followUpsdue.map(insight => {
								const expiresAt = insight.expiresAt instanceof Date ? insight.expiresAt : new Date(insight.expiresAt);
								const daysUntilExpiry = Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
								return (
									<div key={insight.id} className="actionable-item">
										<div className="actionable-info">
											<div className="actionable-title">{insight.title}</div>
											<div className="actionable-customer">
												{getCustomerDisplayName(insight.customerInfo)}
												<span className="expiry-badge">
													{daysUntilExpiry <= 0 ? 'Expired' : daysUntilExpiry === 1 ? 'Due today' : `${daysUntilExpiry} days left`}
												</span>
											</div>
										</div>
										<div className="actionable-actions">
											<ModernButton
												variant="warning"
												size="sm"
												icon="pi pi-phone"
												onClick={() => insight.customerInfo?.phone && handleCallCustomer(insight.customerInfo.phone)}
												disabled={!insight.customerInfo?.phone}
											>
												Follow Up
											</ModernButton>
											<ModernButton
												variant="ghost"
												size="sm"
												icon="pi pi-eye"
												onClick={() => handleViewInsight(insight)}
											/>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</Card>

				{/* Unviewed Insights */}
				<Card className="actionable-card unviewed-card">
					<div className="card-header">
						<h3>
							<i className="pi pi-eye-slash" style={{ marginRight: '0.5rem', color: '#3498db' }}></i>
							New Insights
						</h3>
						<CountIndicator count={unviewedInsights.length} />
					</div>
					{loading ? (
						<div className="actionable-loading">Loading...</div>
					) : unviewedInsights.length === 0 ? (
						<div className="empty-state">
							<i className="pi pi-check" style={{ fontSize: '2rem', color: '#95a5a6' }}></i>
							<p>You've reviewed all insights</p>
						</div>
					) : (
						<div className="actionable-list">
							{unviewedInsights.map(insight => (
								<div key={insight.id} className="actionable-item">
									<div className="actionable-info">
										<div className="actionable-title">
											<span className="new-badge">NEW</span>
											{insight.title}
										</div>
										<div className="actionable-customer">
											{getCustomerDisplayName(insight.customerInfo)}
											<span className="time-badge">{getTimeAgo(insight.createdAt)}</span>
										</div>
									</div>
									<div className="actionable-actions">
										{getPriorityBadge(insight.priority)}
										<ModernButton
											variant="primary"
											size="sm"
											icon="pi pi-eye"
											onClick={() => handleViewInsight(insight)}
										>
											Review
										</ModernButton>
									</div>
								</div>
							))}
						</div>
					)}
				</Card>

				{/* Recent Activity */}
				<Card className="actionable-card activity-card">
					<div className="card-header">
						<h3>
							<i className="pi pi-history" style={{ marginRight: '0.5rem', color: '#16a085' }}></i>
							Recent Activity
						</h3>
						{recentActivity.length > 0 && (
							<ModernButton
								variant="ghost"
								size="sm"
								icon="pi pi-arrow-right"
								onClick={() => navigate('/agent/activities')}
							>
								View All
							</ModernButton>
						)}
					</div>
					{loading ? (
						<div className="actionable-loading">Loading...</div>
					) : recentActivity.length === 0 ? (
						<div className="empty-activity">
							<i className="pi pi-history"></i>
							<p>No recent activity</p>
							<p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#7f8c8d' }}>
								Your interactions will appear here
							</p>
						</div>
				) : (
					<Timeline
						value={recentActivity}
						align="left"
						content={(item: ActivityItem) => (
							<div 
								className="activity-item clickable" 
								onClick={() => handleActivityClick(item)}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										if (e.key === ' ') e.preventDefault();
										handleActivityClick(item);
									}
								}}
							>
								<div className="activity-icon" style={{ backgroundColor: item.color }}>
									<i className={`pi ${item.icon}`}></i>
								</div>
								<div className="activity-content">
									<div className="activity-header">
										<h4>{item.title}</h4>
										<span className="activity-timestamp">{formatTimestamp(item.date)}</span>
									</div>
									<p>{item.description}</p>
								</div>
							</div>
						)}
						className="activity-timeline"
					/>
				)}
				</Card>
			</div>

			{/* Recent Insights */}
			<Card className="insights-card">
				<div className="card-header">
					<h3>Recent Insights</h3>
					<ModernButton
						variant="ghost"
						onClick={() => navigate('/agent/insights')}
					>
						View All
					</ModernButton>
				</div>

				<DataTable
					value={recentInsights}
					loading={loading}
					emptyMessage="No recent insights found."
					className="insights-table"
				>
					<Column
						field="priority"
						header="Priority"
						body={(rowData) => getPriorityBadge(rowData.priority)}
						style={{ width: '100px' }}
					/>
					<Column
						header="Customer"
						body={(rowData) => getCustomerDisplayName(rowData.customerInfo)}
						style={{ width: '150px' }}
					/>
					<Column field="title" header="Insight" />
					<Column
						field="leadScore"
						header="Score"
						body={(rowData) => <CountIndicator count={rowData.leadScore} />}
						style={{ width: '80px' }}
					/>
					<Column
						field="createdAt"
						header="Created"
						body={(rowData) => getTimeAgo(rowData.createdAt)}
						style={{ width: '100px' }}
					/>
					<Column
						header="Actions"
						body={(rowData) => (
							<div className="insight-actions">
								<ModernButton
									variant="ghost"
									size="sm"
									icon="pi pi-eye"
									onClick={() => handleViewInsight(rowData)}
								>
									View
								</ModernButton>
								<ModernButton
									variant="ghost"
									size="sm"
									icon="pi pi-phone"
									onClick={() => rowData.customerInfo?.phone && handleCallCustomer(rowData.customerInfo.phone)}
									disabled={!rowData.customerInfo?.phone}
								>
									Call
								</ModernButton>
								<ModernButton
									variant="ghost"
									size="sm"
									icon="pi pi-envelope"
									onClick={() => rowData.customerInfo?.email && handleEmailCustomer(rowData.customerInfo.email)}
									disabled={!rowData.customerInfo?.email}
								>
									Email
								</ModernButton>
							</div>
						)}
						style={{ width: '200px' }}
					/>
				</DataTable>
			</Card>

			{/* Quick Actions */}
			<Card className="quick-actions-card">
				<h3>Quick Actions</h3>
				<div className="quick-actions">
					<ModernButton
						variant="outline"
						icon="pi pi-plus"
						onClick={() => navigate('/agent/customers/add')}
					>
						Add Customer
					</ModernButton>
					<ModernButton
						variant="outline"
						icon="pi pi-chart-line"
						onClick={() => navigate('/agent/insights')}
					>
						View All Insights
					</ModernButton>
					<ModernButton
						variant="outline"
						icon="pi pi-user"
						onClick={() => navigate('/agent/profile')}
					>
						Manage Profile
					</ModernButton>
					{(currentAgent?.role === 'master_agent' || currentAgent?.role === 'super_agent') && (
						<ModernButton
							variant="outline"
							icon="pi pi-cog"
							onClick={() => navigate('/agent/agents')}
						>
							Admin Panel
						</ModernButton>
					)}
				</div>
			</Card>

			{/* Insight Detail Dialog */}
			<Dialog
				visible={showDetailDialog}
				onHide={() => setShowDetailDialog(false)}
				header={selectedInsight?.title}
				style={{ width: '50vw' }}
				breakpoints={{ '960px': '75vw', '640px': '90vw' }}
			>
				{selectedInsight && (
					<div className="insight-detail">
						<div className="detail-section">
							<h4>Description</h4>
							<p>{selectedInsight.description}</p>
						</div>

						<div className="detail-section">
							<h4>Recommended Action</h4>
							<p>{selectedInsight.contactSuggestion}</p>
						</div>

						<div className="detail-grid">
							<div className="detail-item">
								<label>Priority</label>
								<InsightPriorityIndicator priority={selectedInsight.priority} />
							</div>

							<div className="detail-item">
								<label>Lead Score</label>
								<span className="lead-score-value">{selectedInsight.leadScore}</span>
							</div>

							<div className="detail-item">
								<label>Status</label>
								<span>{selectedInsight.status.replace('_', ' ')}</span>
							</div>

							<div className="detail-item">
								<label>Expires</label>
								<span>{new Date(selectedInsight.expiresAt).toLocaleDateString()}</span>
							</div>
						</div>

						{selectedInsight.customerInfo && (
							<div className="detail-section">
								<h4>Customer Information</h4>
								<div className="customer-info">
									<div className="info-item">
										<label>Name:</label>
										<span>{getCustomerDisplayName(selectedInsight.customerInfo)}</span>
									</div>
									<div className="info-item">
										<label>Email:</label>
										<span>{selectedInsight.customerInfo.email}</span>
									</div>
									{selectedInsight.customerInfo.phone && (
										<div className="info-item">
											<label>Phone:</label>
											<span>{selectedInsight.customerInfo.phone}</span>
										</div>
									)}
									{selectedInsight.customerInfo.bestTimeToContact && (
										<div className="info-item">
											<label>Best Time to Contact:</label>
											<span>{selectedInsight.customerInfo.bestTimeToContact}</span>
										</div>
									)}
									{selectedInsight.customerInfo.preferredContactMethod && (
										<div className="info-item">
											<label>Preferred Contact:</label>
											<span>{selectedInsight.customerInfo.preferredContactMethod}</span>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Same brief as the Insights page, so a lead reads the same in both places. */}
						<LeadBrief insight={selectedInsight} />

						{selectedInsight.data && Object.keys(selectedInsight.data).length > 0 && (
							<div className="detail-section">
								<h4>Additional Data</h4>
								<div className="data-grid">
									{Object.entries(selectedInsight.data).map(([key, value]) => (
										<div key={key} className="data-item">
											<label>{key.replace(/([A-Z])/g, ' $1').trim()}:</label>
											<span>{String(value)}</span>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</Dialog>
		</div>
	);
}; 